import React, { ReactNode } from 'react';

import { Skeleton } from '@mui/material';

import classes from './UploadImageModal.module.scss';
import { LbBigModal } from '../LbBigModal/LbBigModal';
import { UploadImageDropzone } from './UploadImageDropzone/UploadImageDropzone';
import { ImagePreviewCard } from './ImagePreviewCard/ImagePreviewCard';
import { useUploadImage, type UseUploadImageProps } from './hooks/useUploadImage';

/**
 * Reusable modal for image upload with preview, drag-n-drop dropzone, and save/cancel actions.
 * Handles loading state, file validation, and delegates upload/remove/save to provided callbacks.
 *
 * @behavior Changes (upload/clear) are NOT persisted until user clicks "Save".
 * Upload should save image to s3 and on save connect it.
 * Clear actions only update local state and call removeImage on save.
 *
 * @note All API callbacks (getImage, uploadImage, removeImage, saveImage) should
 * set `isRunGlobalSpinner: true` to display loading indicator during requests.
 */
export type UploadImageModalProps = {
  open: boolean;
  title: string;
  description: ReactNode;
  renderPreview: (imageUrl: string) => ReactNode;
  maxFileSizeMb?: number;
  acceptFormats?: string[];
  uploadDescription?: string;
  onClose: () => void;
} & UseUploadImageProps;

const UploadImageModal = ({
  open,
  title,
  description,
  renderPreview,
  maxFileSizeMb,
  acceptFormats,
  uploadDescription,
  onClose,
  getImage,
  uploadImage,
  removeImage,
  saveImage,
}: UploadImageModalProps) => {
  const {
    image,
    isLoading,
    isChanged,
    handleUploadImage,
    handleRemoveImage,
    handleSaveChanges,
  } = useUploadImage({
    getImage,
    uploadImage,
    removeImage,
    saveImage,
  });

  const handleSubmit = () => {
    handleSaveChanges();
    onClose();
  };

  const hasImage = !!image;

  return (
    <LbBigModal
      open={open}
      title={title}
      description={description}
      buttonOptions={{
        noText: 'Cancel',
        noHandler: onClose,
        yesText: 'Save',
        yesHandler: handleSubmit,
        yesDisabled: !isChanged,
      }}
    >
      {isLoading ? (
        <Skeleton variant="rounded" className={classes.skeleton} />
      ) : (
        <>
          {hasImage && (
            <ImagePreviewCard onClear={handleRemoveImage}>
              {renderPreview(image.fileUrl)}
            </ImagePreviewCard>
          )}
          {!hasImage && (
            <UploadImageDropzone
              onUpload={handleUploadImage}
              maxFileSizeMb={maxFileSizeMb}
              acceptFormats={acceptFormats}
              uploadDescription={uploadDescription}
            />
          )}
        </>
      )}
    </LbBigModal>
  );
};

export { UploadImageModal };
